import type { LngLat, WorkArea } from "./types.ts";
import { canvasBearing, canvasHalfSizeM, fromLocalM, toLocalM } from "./work-area.ts";

export interface GrabDelta {
  lng: number;
  lat: number;
}

/** Selected points, plus both ends of each selected line. Each index once, ascending. */
export function grabTargets(
  pointCount: number,
  selection: readonly number[],
  selectedLines: readonly number[],
  segments: ReadonlyArray<readonly [number, number]>,
): number[] {
  const ids = new Set<number>();
  for (const index of selection) {
    if (index >= 0 && index < pointCount) ids.add(index);
  }
  for (const line of selectedLines) {
    const segment = segments[line];
    if (!segment) continue;
    for (const index of segment) {
      if (index >= 0 && index < pointCount) ids.add(index);
    }
  }
  return [...ids].sort((a, b) => a - b);
}

/** Cursor movement since Grab started, in longitude and latitude. */
export function grabDelta(origin: LngLat, cursor: LngLat): GrabDelta {
  return { lng: cursor[0] - origin[0], lat: cursor[1] - origin[1] };
}

/**
 * Grabbed point nearest `at` when the move starts. Equal distances keep the
 * earlier index. `positions` and `at` share one space (screen pixels in the editor).
 */
export function grabAnchor(
  positions: readonly (readonly [number, number] | undefined)[],
  indices: readonly number[],
  at: readonly [number, number],
): number | null {
  let best: number | null = null;
  let bestD = Infinity;
  for (const index of indices) {
    const point = positions[index];
    if (!point) continue;
    const dx = point[0] - at[0];
    const dy = point[1] - at[1];
    const d = dx * dx + dy * dy;
    if (best == null || d < bestD) {
      best = index;
      bestD = d;
    }
  }
  return best;
}

/**
 * Shift the grabbed points by one delta. Snap, when it is on, is applied by
 * the caller to the anchor before this step. With a Canvas, one delta is
 * clamped so every grabbed point stays inside.
 */
export function translateGrab(
  origins: readonly LngLat[],
  indices: readonly number[],
  delta: GrabDelta,
  area: WorkArea | null,
): LngLat[] {
  const clamped = clampGrabDelta(origins, indices, delta, area);
  const moving = new Set(indices);
  return origins.map((point, index) => {
    if (!moving.has(index)) return [point[0], point[1]];
    return [point[0] + clamped.lng, point[1] + clamped.lat];
  });
}

/**
 * Rigid move that lands `anchor` on `target`, then clamps so the whole
 * selection stays inside the canvas. Pass a road-snapped `target` when
 * Snap is on; the other grabbed points keep this same offset.
 */
export function moveGrabTo(
  origins: readonly LngLat[],
  indices: readonly number[],
  anchor: number,
  target: LngLat,
  area: WorkArea | null,
): LngLat[] {
  const origin = origins[anchor];
  if (!origin) return origins.map((point) => [point[0], point[1]]);
  return translateGrab(
    origins,
    indices,
    { lng: target[0] - origin[0], lat: target[1] - origin[1] },
    area,
  );
}

export function grabChanged(before: readonly LngLat[], after: readonly LngLat[]): boolean {
  if (before.length !== after.length) return true;
  for (let i = 0; i < before.length; i++) {
    const a = before[i];
    const b = after[i];
    if (!a || !b || a[0] !== b[0] || a[1] !== b[1]) return true;
  }
  return false;
}

/** One confirmed move pushes the positions from before Grab. A cancel passes changed=false. */
export function commitGrab<T>(past: readonly T[], before: T, changed: boolean, limit: number): T[] {
  if (!changed) return past.slice();
  const next = past.length >= limit ? past.slice(past.length - limit + 1) : past.slice();
  next.push(before);
  return next;
}

export function clampGrabDelta(
  origins: readonly LngLat[],
  indices: readonly number[],
  delta: GrabDelta,
  area: WorkArea | null,
): GrabDelta {
  if (!area || indices.length === 0) return delta;
  if (canvasBearing(area) === 0) {
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    for (const index of indices) {
      const point = origins[index];
      if (!point) continue;
      minLng = Math.min(minLng, point[0]);
      maxLng = Math.max(maxLng, point[0]);
      minLat = Math.min(minLat, point[1]);
      maxLat = Math.max(maxLat, point[1]);
    }
    if (!Number.isFinite(minLng)) return { lng: 0, lat: 0 };
    return {
      lng: clampAxis(minLng, maxLng, delta.lng, area.west, area.east),
      lat: clampAxis(minLat, maxLat, delta.lat, area.south, area.north),
    };
  }

  const { halfW, halfH } = canvasHalfSizeM(area);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let ref: LngLat | null = null;
  for (const index of indices) {
    const point = origins[index];
    if (!point) continue;
    if (!ref) ref = point;
    const [x, y] = toLocalM(point, area);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  if (!ref || !Number.isFinite(minX)) return { lng: 0, lat: 0 };

  const moved: LngLat = [ref[0] + delta.lng, ref[1] + delta.lat];
  const [rx, ry] = toLocalM(ref, area);
  const [mx, my] = toLocalM(moved, area);
  const dLocalX = clampAxis(minX, maxX, mx - rx, -halfW, halfW);
  const dLocalY = clampAxis(minY, maxY, my - ry, -halfH, halfH);
  if (Math.abs(dLocalX) < 1e-12 && Math.abs(dLocalY) < 1e-12) return { lng: 0, lat: 0 };
  const target = fromLocalM([rx + dLocalX, ry + dLocalY], area);
  return { lng: target[0] - ref[0], lat: target[1] - ref[1] };
}

function clampAxis(min: number, max: number, delta: number, lo: number, hi: number): number {
  const low = lo - min;
  const high = hi - max;
  if (low > high) return 0;
  return Math.min(high, Math.max(low, delta));
}
